import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@deltager/organizers/admin/environments/environment';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AffiliateSettings } from '@deltager/organizers/admin/shared/models/AffiliateSettings';
import { AppSettingsService } from '@deltager/organizers/admin/core/app-settings/app-settings.service';
import { retryWithBackoff } from '@deltager/organizers/admin/core/utils/retry-with-backoff';
import { InitFailureService } from './init-failure.service';

@Injectable({
  providedIn: 'root',
})
export class AffiliateStartupService {
  initializedWithAffId: number;

  constructor(
    private http: HttpClient,
    private initFailureService: InitFailureService,
  ) {}

  url = `${environment.apiBaseURL}/affiliates/`;

  private getHttpOptions(): Object {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }

  getSettingsAsync(affId: number): Promise<AffiliateSettings> {
    this.initializedWithAffId = affId;

    // Try to fetch session version; skip the empty-sentinel written on failure
    const cached = this.getLocalStorage(affId);
    if (cached && Object.keys(cached).length > 0) {
      return Promise.resolve(cached);
    }

    return this.http
      .get<AffiliateSettings>(this.url + affId + '/settings', this.getHttpOptions())
      .pipe(
        tap(response => {
          this.setLocalStorage(affId, response);
        }),
        retryWithBackoff(),
        catchError(() => {
          this.initFailureService.affiliateSettingsFailed = true;
          this.setLocalStorage(affId, {} as AffiliateSettings);
          return of({} as AffiliateSettings);
        }),
      )
      .toPromise();
  }

  private setLocalStorage(affId: number, data: Object) {
    const cacheName = `affSettings_${affId}`;
    sessionStorage.setItem(cacheName, JSON.stringify(data));
  }
  private getLocalStorage(affId: number): AffiliateSettings {
    const cacheName = `affSettings_${affId}`;
    const settings = JSON.parse(sessionStorage.getItem(cacheName));
    return settings;
  }
}

export const AffiliateSettingsFactory =
  (affiliateService: AffiliateStartupService, applicationService: AppSettingsService): Function =>
  (): Promise<AffiliateSettings> =>
    affiliateService.getSettingsAsync(applicationService.affiliateId);
